'use client';

/**
 * ClientUsageOverview — operator per-client usage & utilization view.
 * Select an organization → see what's measurable now in one place: seats and
 * active members, the assigned plan tier + estimated recurring charge, per-org
 * transaction fees, and metered consumption for the period (SMS/email/AI/
 * donation fees as they flow). Read-only; the estimate is indicative and feeds
 * the usage→invoice generator (Phase 4).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Building2, Users, UserCheck, Package, Percent, Gauge, FileText } from 'lucide-react';
import RevenueSharePanel from './RevenueSharePanel';
import SeatsLicensesPanel from './SeatsLicensesPanel';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}
const revGet = async <T = any>(p: string): Promise<T | null> => {
  try {
    const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${p}`, { headers: getAuthHeaders() });
    return r.ok ? await r.json() : null;
  } catch { return null; }
};
const revPost = async <T = any>(p: string, body: any): Promise<T> => {
  const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${p}`, {
    method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
  return data as T;
};
const revDelete = async <T = any>(p: string): Promise<T> => {
  const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${p}`, { method: 'DELETE', headers: getAuthHeaders() });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
  return data as T;
};
const revPut = async <T = any>(p: string, body: any): Promise<T> => {
  const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${p}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
  return data as T;
};
const revApi = { get: revGet, post: revPost, del: revDelete, put: revPut };

interface Org { id: number; name: string }
const money = (n: any) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const modelLabel: Record<string, string> = {
  flat_fee: 'Flat fee', per_seat: 'Per user / mo', pmpm: 'Per member / mo',
  tiered: 'Tiered', revenue_share: 'Reimbursement / rev share', hybrid: 'Hybrid',
};

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">{icon}{label}</div>
      <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ClientUsageOverview() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [cboId, setCboId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [monthly, setMonthly] = useState<any>(null);
  const [generating, setGenerating] = useState<string | null>(null); // 'current' | month key
  const [genResult, setGenResult] = useState<any>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setOrgs((Array.isArray(d) ? d : d.cbos || []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, []);

  const load = useCallback(async (cbo: number, keepResult = false) => {
    setLoading(true); setData(null); setMonthly(null); setGenError(null);
    if (!keepResult) setGenResult(null);
    const [ov, mo] = await Promise.all([
      revGet<any>(`/usage/client-overview?cbo_id=${cbo}`),
      revGet<any>(`/usage/monthly?cbo_id=${cbo}&months=12`),
    ]);
    setData(ov);
    setMonthly(mo);
    setLoading(false);
  }, []);

  // Last day of the calendar month that `monthStart` (YYYY-MM-01) falls in.
  const monthEnd = (monthStart: string) => {
    const d = new Date(monthStart + 'T00:00:00');
    return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
  };
  const monthLabel = (monthStart: string) =>
    new Date(monthStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const runGenerate = async (key: string, period_start?: string, period_end?: string, annual = false) => {
    if (!cboId) return;
    setGenerating(key); setGenError(null); setGenResult(null);
    try {
      const res = await revPost<any>('/usage/generate-invoice', { cbo_id: cboId, period_start, period_end, annual });
      await load(cboId, true); // refresh (keep the success banner)
      setGenResult(res);
    } catch (e: any) {
      setGenError(e.message || 'Failed to generate invoice');
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerate = () => runGenerate('current', data?.period?.start, data?.period?.end);
  const isAnnualPlan = !!data?.assigned_tier?.recurring?.annual;

  const tier = data?.assigned_tier;

  return (
    <div className="space-y-5">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Gauge className="h-5 w-5" /> Client Usage &amp; Utilization
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Seats, members, and metered consumption per client for the current period —
            the utilization RevOps bills against.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-gray-400" />
        <select
          value={cboId ?? ''}
          onChange={(e) => { const v = e.target.value ? Number(e.target.value) : undefined; setCboId(v); if (v) load(v); }}
          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-w-[16rem]"
        >
          <option value="">Select an organization…</option>
          {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {cboId && !loading && (
          <button onClick={() => load(cboId)} className="text-sm text-gray-500 hover:text-gray-800 underline">Refresh</button>
        )}
        {cboId && !loading && data && isAnnualPlan && (
          <button
            onClick={() => runGenerate('annual', data?.period?.start, data?.period?.end, true)}
            disabled={!!generating}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:bg-gray-400"
            title="Create the one-time annual prepay invoice (with a revenue-recognition schedule)"
          >
            {generating === 'annual' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Generate annual invoice
          </button>
        )}
        {cboId && !loading && data && (
          <button
            onClick={handleGenerate}
            disabled={!!generating}
            className={`${isAnnualPlan ? '' : 'ml-auto'} inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:bg-gray-400`}
            title="Create a draft invoice from this org's plan + agreed usage for the current period"
          >
            {generating === 'current' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
            Generate draft (this month)
          </button>
        )}
      </div>

      {genError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{genError}</div>
      )}
      {genResult && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-800">
          {genResult.annual_invoice ? 'Annual prepay invoice' : 'Draft invoice'}{' '}
          <span className="font-semibold">{genResult.invoice_number}</span> created —
          {' '}{genResult.line_count} line(s), {money(genResult.total)}
          {genResult.annual_invoice && genResult.recognition_method
            ? `, recognized ${genResult.recognition_method}${genResult.recognition_rows ? ` (${genResult.recognition_rows} schedule row${genResult.recognition_rows > 1 ? 's' : ''})` : ''}`
            : ''}. Review and release it in Billing → Invoices.
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 py-10"><Loader2 className="h-5 w-5 animate-spin" /> Loading usage…</div>
      )}

      {!loading && cboId && data && (
        <div className="space-y-5">
          <div className="text-xs text-gray-400">
            Period: {data.period?.start} → {data.period?.end}
            {!data.billing_provisioned && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">metered billing not provisioned — counts only</span>}
          </div>

          {/* Utilization + estimate */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={<Users className="h-3.5 w-3.5" />} label="Seats (users)" value={String(data.counts?.seats ?? 0)} />
            <Stat icon={<UserCheck className="h-3.5 w-3.5" />} label="Active members" value={String(data.counts?.active_members ?? 0)} />
            <Stat
              icon={<Package className="h-3.5 w-3.5" />}
              label="Est. recurring / mo"
              value={money(data.estimated_recurring)}
              sub={tier
                ? (tier.recurring?.annual
                    ? `${tier.tier_name} · billed ${money(tier.recurring.annual_amount)}/yr`
                    : `${tier.tier_name} · ${modelLabel[tier.pricing_model] || tier.pricing_model}`)
                : 'No plan assigned'}
            />
            <Stat
              icon={<Gauge className="h-3.5 w-3.5" />}
              label={tier?.recurring?.annual ? 'Est. total (annual)' : 'Est. total (period)'}
              value={money(tier?.recurring?.annual
                ? (tier.recurring.annual_amount || 0) + (data.metered_total || 0)
                : data.estimated_total)}
              sub={`incl. ${money(data.metered_total)} metered`}
            />
          </div>

          {/* Assigned tier detail */}
          {tier && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Plan tier</h3>
              <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Tier</span><span className="font-medium">{tier.tier_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Model</span><span className="font-medium">{modelLabel[tier.pricing_model] || tier.pricing_model}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Base / mo</span><span className="font-medium">{money(tier.recurring?.base ?? tier.base_monthly_price)}</span></div>
                {(tier.recurring?.staff_rate ?? 0) > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500">Per staff / mo</span><span className="font-medium">{money(tier.recurring.staff_rate)} × {tier.recurring.staff_count}</span></div>
                )}
                {(tier.recurring?.member_rate ?? 0) > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500">Per member / mo</span><span className="font-medium">{money(tier.recurring.member_rate)} × {tier.recurring.member_count}</span></div>
                )}
              </div>
            </div>
          )}

          {/* Metered usage */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
              Metered consumption
            </div>
            {(!data.metered_usage || data.metered_usage.length === 0) ? (
              <p className="px-4 py-4 text-sm text-gray-500">No metered usage recorded for this period.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Meter</th>
                    <th className="text-right px-4 py-2 font-medium">Total</th>
                    <th className="text-right px-4 py-2 font-medium">Billable</th>
                    <th className="text-right px-4 py-2 font-medium">Unit price</th>
                    <th className="text-right px-4 py-2 font-medium">Amount</th>
                    <th className="text-right px-4 py-2 font-medium">Invoiced</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.metered_usage.map((m: any) => (
                    <tr key={m.meter_code}>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                        {m.meter_name || m.meter_code}
                        {!m.agreed && (
                          <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700" title="No per-org contracted rate — not billed">not contracted</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{Number(m.total_usage).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{Number(m.billable_usage).toLocaleString()}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">${Number(m.unit_price).toLocaleString(undefined, { maximumFractionDigits: 6 })}</td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        {m.agreed ? money(m.agreed_amount) : <span className="text-gray-400">{money(m.total_amount)}</span>}
                      </td>
                      <td className="px-4 py-2 text-right">{m.invoiced ? <span className="text-xs text-green-600">yes</span> : <span className="text-xs text-gray-400">no</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Transaction fees */}
          {data.fees && data.fees.length > 0 && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1.5"><Percent className="h-3.5 w-3.5" /> Transaction fees</h3>
              <div className="space-y-1 text-sm">
                {data.fees.map((f: any) => (
                  <div key={f.service_type} className="flex justify-between">
                    <span className="text-gray-500 capitalize">{String(f.service_type).replace(/_/g, ' ')}</span>
                    <span className="font-medium">
                      {(f.fee_percent * 100).toFixed(2)}%{f.fee_fixed_cents ? ` + ${money(f.fee_fixed_cents / 100)}` : ''}
                      <span className="ml-2 text-xs text-gray-400">({String(f.billing_model).replace(/_/g, ' ')})</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Monthly history — decide which months to bill */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center justify-between">
              <span>Monthly usage history</span>
              <span className="text-xs font-normal text-gray-400">Recurring = staff + members at each month's headcount. Only contracted (agreed) metered usage is billed.</span>
            </div>
            {(!monthly?.months || monthly.months.length === 0) ? (
              <p className="px-4 py-4 text-sm text-gray-500">No monthly history yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Month</th>
                    <th className="text-right px-4 py-2 font-medium">Staff</th>
                    <th className="text-right px-4 py-2 font-medium">Members</th>
                    <th className="text-right px-4 py-2 font-medium">Recurring</th>
                    <th className="text-right px-4 py-2 font-medium">Metered (agreed)</th>
                    <th className="text-right px-4 py-2 font-medium">Est. total</th>
                    <th className="text-center px-4 py-2 font-medium">Status</th>
                    <th className="text-right px-4 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {monthly.months.map((mo: any) => {
                    const badge =
                      mo.billed === 'billed' ? 'bg-green-100 text-green-700'
                      : mo.billed === 'partial' ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-600';
                    const busy = generating === mo.month;
                    const hasDetail = (mo.metered_usage || []).length > 0;
                    const open = expandedMonth === mo.month;
                    return (
                      <React.Fragment key={mo.month}>
                      <tr>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {hasDetail ? (
                            <button onClick={() => setExpandedMonth(open ? null : mo.month)} className="inline-flex items-center gap-1 hover:text-indigo-600" title="Show usage detail (addendum)">
                              <span className={`transition-transform ${open ? 'rotate-90' : ''}`}>▸</span>{monthLabel(mo.month)}
                            </button>
                          ) : monthLabel(mo.month)}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{mo.seats ?? 0}</td>
                        <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{mo.active_members ?? 0}</td>
                        <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{money(mo.recurring_total)}</td>
                        <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{money(mo.metered_agreed_total)}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100">{money(mo.estimated_total)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${badge}`}>{mo.invoice ? 'created' : mo.billed}</span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {mo.invoice ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                              <FileText className="h-3 w-3" />
                              {mo.invoice.invoice_number}
                              <span className="rounded bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 capitalize">{mo.invoice.status}</span>
                            </span>
                          ) : (
                            <button
                              onClick={() => runGenerate(mo.month, mo.month, monthEnd(mo.month))}
                              disabled={!!generating}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-md hover:bg-indigo-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              title="Generate a draft invoice for this month"
                            >
                              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                              Generate draft
                            </button>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-gray-50/60 dark:bg-gray-900/30">
                          <td colSpan={8} className="px-6 py-3">
                            <div className="text-xs font-medium text-gray-500 mb-2">
                              Usage detail — {monthLabel(mo.month)} (addendum{mo.invoice ? ` for ${mo.invoice.invoice_number}` : ''})
                            </div>
                            <table className="w-full text-xs">
                              <thead className="text-gray-400">
                                <tr>
                                  <th className="text-left py-1 font-medium">Meter</th>
                                  <th className="text-right py-1 font-medium">Total</th>
                                  <th className="text-right py-1 font-medium">Billable</th>
                                  <th className="text-right py-1 font-medium">Unit price</th>
                                  <th className="text-right py-1 font-medium">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(mo.metered_usage || []).map((m: any) => (
                                  <tr key={m.meter_code}>
                                    <td className="py-1 text-gray-700 dark:text-gray-300">
                                      {m.meter_name || m.meter_code}
                                      {!m.agreed && <span className="ml-2 text-amber-600">(not contracted)</span>}
                                    </td>
                                    <td className="py-1 text-right text-gray-500">{Number(m.total_usage).toLocaleString()}</td>
                                    <td className="py-1 text-right text-gray-500">{Number(m.billable_usage).toLocaleString()}</td>
                                    <td className="py-1 text-right text-gray-500">${Number(m.unit_price).toLocaleString(undefined, { maximumFractionDigits: 6 })}</td>
                                    <td className="py-1 text-right font-medium text-gray-800 dark:text-gray-200">{m.agreed ? money(m.agreed_amount) : <span className="text-gray-400">{money(m.total_amount)}</span>}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {!loading && cboId && data && <SeatsLicensesPanel cboId={cboId} api={revApi} />}

      {!loading && cboId && data && (
        <RevenueSharePanel
          cboId={cboId}
          api={revApi}
          sharePercent={tier?.revenue_share_config?.reimbursement_share_percent ?? null}
        />
      )}

      {!loading && !cboId && (
        <p className="text-sm text-gray-500 py-8">Select an organization to view its usage and utilization.</p>
      )}
    </div>
  );
}
